import { FC } from "react";
import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import forbiddenIcon from "../../assets/forbidden-icon.svg";
import notFoundIcon from "../../assets/notFound-icon.svg";
import unauthorizedIcon from "../../assets/unauthorized-icon.svg";
import unknownIcon from "../../assets/unknow-icon.svg";
import ErrorMessageActions from "./ErrorMessageActions";

export type ReloadAction = {
	title?: string;
	onClick: () => void;
};

type ErrorMessageProps = {
	actions?: Array<ReloadAction>;
	httpStatusCode?: number;
};

type ErrorIconConfig = {
	src: string;
	alt: string;
};

const ERROR_ICONS: Partial<Record<number, ErrorIconConfig>> = {
	401: {
		src: unauthorizedIcon,
		alt: "Unauthorized",
	},
	403: {
		src: forbiddenIcon,
		alt: "Forbidden",
	},
	404: {
		src: notFoundIcon,
		alt: "Not found",
	},
};

const DEFAULT_ERROR_ICON: ErrorIconConfig = {
	src: unknownIcon,
	alt: "Unknown error",
};

/**
 * Displays an HTTP error message and an optional retry action.
 */
const ErrorMessage: FC<ErrorMessageProps> = ({ httpStatusCode, actions = [] }) => {
	const { t } = useTranslation("errors");

	const messageKey = httpStatusCode ? `http/${httpStatusCode}` : "unknown";
	const icon = httpStatusCode ? (ERROR_ICONS[httpStatusCode] ?? DEFAULT_ERROR_ICON) : DEFAULT_ERROR_ICON;

	return (
		<Stack spacing={2} alignItems="center" justifyContent="center" height="100%" padding={3} textAlign="center">
			<img src={icon.src} alt={icon.alt} width={100} height={100} />

			<Typography variant="h5" component="h1">
				{t(messageKey, { context: "title" })}
			</Typography>

			<Typography color="text.secondary" maxWidth={500}>
				{t(messageKey, { context: "message" })}
			</Typography>

			<ErrorMessageActions actions={actions} />
		</Stack>
	);
};

export default ErrorMessage;
